import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Grid } from '@mui/material';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useOrderDetails } from 'components/context/order_details';
import { useUserJourneys } from 'components/context/user_journeys';
import {
  onCheckoutPageClick,
  onCheckoutPageView,
  onCheckoutPageViewV2,
  pushHistoryWithSearchParams,
} from 'components/pages/checkout/utilities';
import AddressForm from 'components/common/address_form';
import POBoxMessage from 'components/pages/checkout/pobox_message';
import ControlledInputPhone from 'components/common/forms/controlled_input_phone';
import ControlledInputText from 'components/common/forms/controlled_input_text';
import NameFields from 'components/common/name_fields';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import { isFeatureEnabled, PO_BOX_CHECK } from 'utilities/features';
import { isPOBoxAddress } from 'utilities/po_box';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import { EMAIL_REGEX, TAX_NUMBER_REGEX_BY_COUNTRY } from 'constants/patterns';
import { INSTALLMENTS_LOGIN_QUERY_PARAM } from 'components/pages/checkout/payment_option';
import { ISSUER_INSTALLMENTS_LOGIN_QUERY_PARAM } from 'components/pages/checkout/payment';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import { COMPONENT_NAMES } from 'constants/tracking';
import { UserJourneyName, FrustrationSignal } from 'constants/user_journeys';
import {
  kanjiToISO,
  useAddressValidation,
  VALIDATION_STATUS_TO_CDP_NAME,
} from 'components/pages/checkout/hooks/use_address_validation';
import { useFeatureFlags } from 'components/context/feature_flags';
import ShippingAddressSelectionModal from 'components/pages/checkout/components/shipping_address_selection_modal';
import { trackCdp } from 'utilities/tracking';
import { SHIPPING_INFORMATION_VALIDATION } from 'constants/cdp_events';
import { getCountryCode } from 'utilities/locales';

const Information = ({ location }) => {
  const infoForm = useForm();
  const history = useHistory();
  const countryCode = getCountryCode().toUpperCase();
  const { addressValidationEnabled } = useFeatureFlags();

  const [viewPOBox, setViewPOBox] = useState(false);
  const [viewAddressSelectionModal, setViewAddressSelectionModal] =
    useState(false);

  const { t } = useTranslation();
  const { number: orderNumber, store, trackingData } = useOrderDetails();
  const {
    checkoutClient,
    orderData,
    orderRefresh,
    setToast,
    setShippingAddress,
    setAddressValidationResponse,
    setAddressValidationStatus,
  } = useCheckoutFlow();
  const { userJourneyTracker } = useUserJourneys();
  const { isAddressValidating, validateAddress } = useAddressValidation();

  const error = location?.state?.error;
  const isWarranty = orderData.is_warranty;

  const removeQueryParam = (param) => {
    const urlSearchParams = new URLSearchParams(history.location.search);
    urlSearchParams.delete(param);
    history.replace(`${history.location.pathname}?${urlSearchParams}`);
  };

  useEffect(() => {
    if (userJourneyTracker) {
      userJourneyTracker.startJourney({
        userJourneyName: UserJourneyName.ShopPlaceOrder,
      });
    }

    // check for key query params for possible redirects
    const queryParams = new URLSearchParams(history.location.search);
    const isInstallmentsLogin = queryParams.has(INSTALLMENTS_LOGIN_QUERY_PARAM);
    const isIssuerInstallmentsLogin = queryParams.has(
      ISSUER_INSTALLMENTS_LOGIN_QUERY_PARAM,
    );

    const isUserLoggedIn = orderData.current_user?.is_logged_in;
    // naively determine checkout steps completed
    const isContactInfoComplete = Object.values(
      orderData.contact_information,
    ).every((v) => v !== '');
    const areAddressesComplete =
      Object.keys(orderData.shipping_address).length > 0 &&
      Object.keys(orderData.billing_address).length > 0;
    // check if user has payment with issuer installments and greater than 1
    const hasPaymentWithIssuerInstallments =
      (orderData.issuer_installments_summary?.number_of_payments_selected ??
        1) > 1;

    if (isUserLoggedIn && isContactInfoComplete && areAddressesComplete) {
      if (isInstallmentsLogin) {
        pushHistoryWithSearchParams(history, '/payment_option');
        return;
      } else if (isIssuerInstallmentsLogin) {
        removeQueryParam(ISSUER_INSTALLMENTS_LOGIN_QUERY_PARAM);

        const paymentCoversOrderTotal =
          orderData.payment_information?.payment_covers_order_total;

        // value could be "null" which is not the same as false
        if (paymentCoversOrderTotal === false) {
          setToast({
            open: true,
            message: t(
              'checkout.payment.error.order_total_not_covered_by_payment',
            ),
            variant: 'warning',
          });
        }

        pushHistoryWithSearchParams(
          history,
          hasPaymentWithIssuerInstallments && paymentCoversOrderTotal === true
            ? '/review'
            : '/payment_option',
        );
        return;
      }
    }

    onCheckoutPageView('Shipping Information', trackingData);
    onCheckoutPageViewV2({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT.CONTACT_AND_SHIPPING,
      additionalParameters: trackingData,
    });
  }, []);

  useEffect(() => {
    if (error) {
      setToast({
        open: true,
        message: t(`checkout.error.${error}`),
        variant: 'critical',
      });

      userJourneyTracker.addFrustrationSignal({
        userJourneyName: UserJourneyName.ShopPlaceOrder,
        frustrationSignal: FrustrationSignal.InformationStepUnknownError,
      });
    }
  }, [error]);

  const updateOrderInformation = (addressData) => {
    return checkoutClient
      .orderUpdate(addressData)
      .then(orderRefresh)
      .then(() => {
        if (isWarranty || orderData.no_ship) {
          onCheckoutPageClick({
            eventDesc:
              'Submit contact and shipping information and continue to payment',
            componentName: COMPONENT_NAMES.APPS.CHECKOUT.CONTACT_AND_SHIPPING,
            actionItem: 'continue_to_payment',
            trackingData,
          });
          pushHistoryWithSearchParams(history, '/payment');
        } else {
          onCheckoutPageClick({
            eventDesc:
              'Submit contact and shipping information and continue to shipping',
            componentName: COMPONENT_NAMES.APPS.CHECKOUT.CONTACT_AND_SHIPPING,
            actionItem: 'continue_to_shipping',
            trackingData,
          });
          pushHistoryWithSearchParams(history, '/shipping_method');
        }
      })
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal: FrustrationSignal.InformationStepOrderRefreshError,
        });
        setToast({
          open: true,
          message: t('error.api.addressUpdate'),
          variant: 'critical',
        });
      });
  };

  const checkPOBoxVisbility = (addressData, checkPOBox) => {
    const poBoxEnabled = isFeatureEnabled(PO_BOX_CHECK);
    const isPoBox = isPOBoxAddress(store, [
      addressData['address_line_1'],
      addressData['address_line_2'],
      addressData['company'],
    ]);

    return poBoxEnabled && checkPOBox && isPoBox;
  };

  // addressData includes both the shipping and billing address from the form
  const submit = async (addressData, checkPOBox) => {
    // 'Continue to Shipping' vs 'Continue with PO Box'
    if (checkPOBoxVisbility(addressData, checkPOBox)) {
      return setViewPOBox(true);
    }

    // attempt to validate the address
    try {
      if (addressValidationEnabled) {
        const {
          addressValidationResponse,
          addressIsVerified,
          validationStatus,
          correctedAddress,
          trackingParams,
        } = await validateAddress(addressData);

        trackCdp({
          name: SHIPPING_INFORMATION_VALIDATION.REQUEST_SUCCESS,
          params: { ...trackingParams, order_id: orderNumber },
        });

        trackCdp({
          name: VALIDATION_STATUS_TO_CDP_NAME[validationStatus],
          params: { ...trackingParams, order_id: orderNumber },
        });

        setAddressValidationResponse(addressValidationResponse);
        setAddressValidationStatus(validationStatus);

        // 1. if the address is not verified, we show the modal
        if (!addressIsVerified) {
          return setViewAddressSelectionModal(true);
        }

        // 2. else the address is verified, we update the order information with the corrected address
        const correctedAddressData = {
          ...addressData, // spread the original address information so we can get billing info
          ...correctedAddress, // spread the corrected address info
        };

        // update the form values with the corrected address
        setShippingAddress(correctedAddress);
        return updateOrderInformation(correctedAddressData);
      }

      // if address validation is disabled, just update the order information as usual
      return updateOrderInformation(addressData);
    } catch {
      trackCdp({
        name: SHIPPING_INFORMATION_VALIDATION.REQUEST_ERROR,
        params: {
          order_number: orderNumber,
        },
      });

      // do not block the user from continuing if we error out on getting eligibility or validating the address
      return updateOrderInformation(addressData);
    }
  };

  const handleModalContinue = (address) => {
    const originalAdminDistrictLvl1 = address.administrative_district_level_1;
    const formattedAdminDistrictLvl1 =
      countryCode === 'JP'
        ? kanjiToISO(originalAdminDistrictLvl1)
        : originalAdminDistrictLvl1;

    // update shippingAddress state in context
    setShippingAddress({
      ...address,
      administrative_district_level_1: formattedAdminDistrictLvl1,
    });

    // update form data with selected address
    infoForm.setValue('address_line_1', address.address_line_1);
    infoForm.setValue('address_line_2', address.address_line_2);
    infoForm.setValue('locality', address.locality);
    infoForm.setValue('postal_code', address.postal_code);
    infoForm.setValue(
      'administrative_district_level_1',
      formattedAdminDistrictLvl1,
    );

    // submit the form
    infoForm.handleSubmit((data) => updateOrderInformation(data))();
  };

  return (
    <>
      <Box component="h2" sx={{ mb: 2 }}>
        {t(`checkout.information.${isWarranty ? 'titleAlt' : 'title'}`)}
      </Box>
      <Grid container spacing={2} justifyContent="flex-end">
        {isWarranty && (
          <Grid item xs={12} md={12}>
            <h3>{t('checkout.information.explanation')}</h3>
            <p>{t('checkout.information.description')}</p>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <ControlledInputText
            name="email"
            control={infoForm.control}
            defaultValue={orderData.contact_information?.email}
            rules={{
              required: true,
              pattern: {
                value: EMAIL_REGEX,
                message: t('error.validation.invalidEmail'),
              },
            }}
            label={t('checkout.label.email')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledInputPhone
            name="phone"
            control={infoForm.control}
            defaultValue={orderData.contact_information?.phone}
            rules={{
              required: true,
            }}
            maxlength={20}
            label={t('checkout.label.phone')}
            store={store}
          />
        </Grid>
        {TAX_NUMBER_REGEX_BY_COUNTRY[store] && (
          <Grid item xs={12} md={12}>
            <ControlledInputText
              name="tax_number"
              control={infoForm.control}
              defaultValue={orderData.tax_number_information.number}
              rules={{
                pattern: {
                  value: TAX_NUMBER_REGEX_BY_COUNTRY[store],
                  message: t('error.validation.eu.invalidVatNumber'),
                },
                validate: (value) =>
                  value ? checkoutClient.validateTaxNumber(value) : true,
              }}
              label={t('checkout.label.eu.vatNumberOptional')}
            />
          </Grid>
        )}
        <FormProvider {...infoForm}>
          <NameFields address={orderData.shipping_address} store={store} />
          <AddressForm
            handleSubmit={infoForm.handleSubmit((addressData) =>
              submit(addressData, true),
            )}
            disableCta={isAddressValidating}
          />
        </FormProvider>
      </Grid>
      <POBoxMessage
        open={viewPOBox}
        handleContinue={infoForm.handleSubmit((data) => {
          setViewPOBox(false);
          submit(data, false);
        })}
        handleDismiss={() => {
          setViewPOBox(false);
        }}
      />
      <ShippingAddressSelectionModal
        open={viewAddressSelectionModal}
        handleContinue={handleModalContinue}
        handleDismiss={() => {
          setViewAddressSelectionModal(false);
        }}
      />
      {isDashboardMode() && (
        <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
      )}
    </>
  );
};

export default Information;
